import dayjs from 'dayjs'
import {
    Calendar as BigCalendar,
    CalendarProps,
    Components,
    dayjsLocalizer,
    EventProps,
    ShowMoreProps
} from 'react-big-calendar'
import withDragAndDrop, {EventInteractionArgs} from 'react-big-calendar/lib/addons/dragAndDrop'
import {ComponentType, useMemo} from 'react'
import type {
    AgendaTeamResource,
    AgendaEventType,
    AgendaView,
    AgendaEventAppointment,
    AgendaEventGeneric,
    AgendaEventAbsence,
    AgendaEventUnavailability
} from '@/features/agenda/types'
import {AgendaHeaderDay} from '@/features/agenda/components/agenda-header-day/AgendaHeaderDay'
import {formatLocaleDate} from '@/dayjs'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {
    StyledAgendaCalendarLoadingWrapper,
    StyledAgendaCalendarLockOverlay,
    StyledAgendaMonthHeader
} from '@/features/agenda/components/agenda-calendar/style'
import {useTranslation} from '@/translations/i18n'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {AgendaEvent} from '@/features/agenda/components/agenda-event/AgendaEvent'
import {AgendaResourceHeader} from '@/features/agenda/components/agenda-resource-header/AgendaResourceHeader'
import {useRetrieveResources} from '@/features/agenda/hooks/useRetrieveResources'
import {AgendaScheduleDate} from '@/features/agenda/components/agenda-schedule-date/AgendaScheduleDate'
import {AgendaScheduleTime} from '@/features/agenda/components/agenda-schedule-time/AgendaScheduleTime'
import {AgendaScheduleEvent} from '@/features/agenda/components/agenda-schedule-event/AgendaScheduleEvent'
import {useCalendarData} from '@/features/agenda/hooks/useCalendarData'
import {useCalendarConfiguration} from '@/features/agenda/hooks/useCalendarConfiguration'
import {useEventHandlers} from '@/features/agenda/hooks/useEventHandlers'
import {AgendaCalendarShowMoreEvents} from '@/features/agenda/components/agenda-calendar-show-more-events/AgendaCalendarShowMoreEvents'
import {AgendaCalendarToolbar} from '@/features/agenda/components/agenda-calendar-toolbar/AgendaCalendarToolbar'
import {AgendaCalendarHeader} from '@/features/agenda/components/agenda-calendar-header/AgendaCalendarHeader'
import {useSocketAgendaEvents} from '@/features/agenda/hooks/useSocketAgendaEvents'
import {CreateEventFormDialog} from '@/features/agenda/components/create-event-form-dialog/CreateEventFormDialog'
import {useDialogStore} from '@ariakit/react'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekday from 'dayjs/plugin/weekday'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {usePermissions} from '@/features/auth/store/store'

// Set the start of the week to Monday
dayjs.extend(weekOfYear)
dayjs.extend(weekday)
dayjs.Ls.en.weekStart = 1
const localizer = dayjsLocalizer(dayjs)

// BigCalendar is not typed for AgendaEventType, so we need to cast it to the correct type
const AgendaBigCalendar = BigCalendar as ComponentType<CalendarProps<AgendaEventType>>
const AgendaDndCalendar = withDragAndDrop(AgendaBigCalendar)

interface AgendaCalendarProps {
    date: string
    view: AgendaView
    appointments: AgendaEventAppointment[]
    blockouts: AgendaEventAbsence[]
    unavailabilities: AgendaEventUnavailability[]
}

export const AgendaCalendar = ({date, view, appointments, blockouts, unavailabilities}: AgendaCalendarProps) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const resources = useRetrieveResources()
    const {
        searchParams: {subjectIds}
    } = useAgendaSearchParams()

    // Custom hook for calendar data
    const {storedAppointments, storedBlockouts, storedUnavailabilities, isDisabledDay} = useCalendarData({
        appointments,
        blockouts,
        unavailabilities
    })

    // Custom hook for calendar configuration
    const {
        parsedDate,
        minTime,
        maxTime,
        formats,
        dayPropGetter,
        eventPropGetter,
        validateEventMove,
        validateEventResize,
        validateSlotSelection,
        timeSlots,
        stepMinutes,
        resizable
    } = useCalendarConfiguration({
        date,
        view,
        storedBlockouts,
        storedUnavailabilities,
        isDisabledDay
    })

    // Custom hook for event handlers
    const {
        handleMoveEvent,
        handleResizeEvent,
        handleSelectSlot,
        isUpdatingEvent,
        selectedCalendarSlot,
        setSelectedCalendarSlot
    } = useEventHandlers({
        blockouts,
        validateEventMove,
        validateEventResize,
        validateSlotSelection
    })

    // Calendar navigation
    const {
        navigateWithParams,
        navigateToView,
        navigateToDate,
        params: {entity}
    } = useAgendaNavigation()

    // Custom hook for socket agenda events
    useSocketAgendaEvents()

    // Handle the creation of a new event from the calendar
    const dialogStore = useDialogStore({
        open: !!selectedCalendarSlot,
        setOpen: () => setSelectedCalendarSlot(null)
    })

    // Components
    const components = useMemo<Components<AgendaEventType>>(
        () => ({
            header: AgendaCalendarHeader,
            toolbar: AgendaCalendarToolbar,
            showMore: (event: ShowMoreProps<AgendaEventType>) => <AgendaCalendarShowMoreEvents event={event} />,
            resourceHeader: props => {
                // Cast the resource to AgendaTeamResource since we know its structure
                const resource = props.resource as AgendaTeamResource
                return <AgendaResourceHeader resource={resource} />
            },
            week: {
                header: ({date}: {date: Date}) => <AgendaHeaderDay date={date} formatDay="ddd" formatDayNumber="D" />,
                event: (props: EventProps<AgendaEventType>) => {
                    return <AgendaEvent props={props} />
                }
            },
            month: {
                dateHeader: ({date}: {date: Date}) => <AgendaHeaderDay date={date} formatDayNumber="D" />,
                header: ({date}: {date: Date}) => (
                    <StyledAgendaMonthHeader weight="400">{formatLocaleDate(date, 'ddd')}</StyledAgendaMonthHeader>
                ),
                event: (props: EventProps<AgendaEventType>) => {
                    return <AgendaEvent props={props} isSmall />
                }
            },
            day: {
                header: ({date}: {date: Date}) => <AgendaHeaderDay date={date} formatDay="ddd" formatDayNumber="D" />,
                event: (props: EventProps<AgendaEventType>) => <AgendaEvent props={props} />
            },
            agenda: {
                date: props => {
                    const date = (props as {day: Date}).day
                    // Don't show disabled days in agenda view
                    if (isDisabledDay(date)) return null
                    return <AgendaScheduleDate date={date} />
                },
                time: props => {
                    const {
                        event: {start, end, status}
                    } = props as EventProps<AgendaEventAppointment>

                    return <AgendaScheduleTime startDate={start} endDate={end} status={status} />
                },
                event: props => {
                    const event = props.event as AgendaEventAppointment
                    return <AgendaScheduleEvent event={event} />
                }
            }
        }),
        [entity, isDisabledDay, navigateWithParams, t]
    )

    // Messages
    const messages = useMemo(
        () => ({
            noEventsInRange: t('agenda:no_events_in_range')
        }),
        []
    )

    return (
        <>
            {isUpdatingEvent && (
                <StyledAgendaCalendarLoadingWrapper>
                    <Spinner size={16} />
                </StyledAgendaCalendarLoadingWrapper>
            )}

            {entity == 'patients' && (!subjectIds || subjectIds?.length == 0) && <StyledAgendaCalendarLockOverlay />}

            <AgendaDndCalendar
                localizer={localizer}
                date={parsedDate}
                view={view}
                className={`agenda-calendar ${view} ${view == 'week' ? 'hide-holidays' : ''}`}
                components={components}
                events={storedAppointments}
                backgroundEvents={[...storedBlockouts, ...storedUnavailabilities]}
                formats={formats}
                min={minTime}
                onView={view => navigateToView(view as AgendaView)}
                onNavigate={date => navigateToDate(date)}
                onEventDrop={event =>
                    (event.event.type == 'event' || event.event.type == 'generic_event') &&
                    handleMoveEvent(event as EventInteractionArgs<AgendaEventAppointment | AgendaEventGeneric>)
                }
                onEventResize={event =>
                    (event.event.type == 'event' || event.event.type == 'generic_event') &&
                    handleResizeEvent(event as EventInteractionArgs<AgendaEventAppointment | AgendaEventGeneric>)
                }
                max={maxTime}
                timeslots={timeSlots}
                step={stepMinutes}
                draggableAccessor={permissions?.agenda.write ? 'isDraggable' : () => false}
                resizable={permissions?.agenda.write ? resizable : false}
                resources={resources?.length ? resources : undefined}
                selectable={permissions?.agenda.write}
                onSelectSlot={permissions?.agenda.write ? handleSelectSlot : undefined}
                views={['month', 'week', 'day', 'agenda']}
                messages={messages}
                dayPropGetter={dayPropGetter}
                eventPropGetter={eventPropGetter}
                // dayLayoutAlgorithm="no-overlap" // Prevent events from overlapping on the same day
                resourceIdAccessor={resource => {
                    const typedResource = resource as {resourceId: string; resourceTitle: string}
                    return typedResource.resourceId
                }}
                resourceTitleAccessor={resource => {
                    const typedResource = resource as {resourceId: string; resourceTitle: string}
                    return typedResource.resourceTitle
                }}
            />
            {selectedCalendarSlot && (
                <CreateEventFormDialog defaultValues={selectedCalendarSlot} providedDialogStore={dialogStore} />
            )}
        </>
    )
}
